import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadLiveness } from "src/services/LivenessService";
import type { LivenessAggregate } from "src/services/livenessAggregated";

const REFRESH_INTERVAL = 60_000;
const REQUEST_TIMEOUT = 20_000;

export function useLiveness() {
	const localeRevision = useLocale();
	const [hours, setHours] = useState(24);
	const [records, setRecords] = useState<LivenessAggregate | null>(null);
	const [busy, setBusy] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
	const active = useRef<AbortController | null>(null);
	const mounted = useRef(false);

	const refresh = useCallback(async () => {
		if (!mounted.current || active.current) return;
		const controller = new AbortController();
		active.current = controller;
		setBusy(true);
		const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
		try {
			const data = await loadLiveness(controller.signal, hours);
			if (mounted.current && active.current === controller && !controller.signal.aborted) {
				setRecords(data);
				setUpdatedAt(new Date());
				setError(null);
			}
		} catch (cause) {
			if (mounted.current && active.current === controller) {
				setError(controller.signal.aborted ? msg("serviceStatusRequestTimedOut") : cause instanceof Error ? cause.message : msg("couldNotLoadServiceStatus"));
			}
		} finally {
			window.clearTimeout(timeout);
			if (mounted.current && active.current === controller) {
				// The shared HTTP client can resolve an aborted request as an API error.
				if (controller.signal.aborted) setError(msg("serviceStatusRequestTimedOut"));
				active.current = null;
				setBusy(false);
			}
		}
	}, [localeRevision, hours]);

	useEffect(() => {
		mounted.current = true;
		setRecords(null);
		setUpdatedAt(null);
		setError(null);
		void refresh();
		const refreshVisible = () => { if (!document.hidden) void refresh(); };
		const interval = window.setInterval(refreshVisible, REFRESH_INTERVAL);
		document.addEventListener("visibilitychange", refreshVisible);
		return () => {
			mounted.current = false;
			active.current?.abort();
			active.current = null;
			window.clearInterval(interval);
			document.removeEventListener("visibilitychange", refreshVisible);
		};
	}, [refresh]);

	return { records, busy, error, updatedAt, refresh, hours, setHours };
}
